import type { Category, CategorySummary, FindingModel } from '@/types/documents.types';
import { sourceLabelKey } from '@/utils/categories';

/**
 * How the review reads a finding — the one place that knows the finding's wire shape
 * (`categories[]`, `item_id`, `item_name`, `sources`).
 */

type Translate = (key: string, options?: { defaultValue?: string }) => string;

/** Every category the finding belongs to. */
export const findingCategories = (finding: FindingModel): Category[] => finding.categories;

/** The name of the item that found it ("Rodné číslo"); empty for declarations and manual findings. */
export const findingItemName = (finding: FindingModel): string => finding.item_name ?? '';

/** How it was found ("regex", "LLM", "Číselník", "ručně"; merged spans "regex + LLM"). */
export const findingMethod = (finding: FindingModel, t: Translate): string =>
  finding.sources.map((source) => t(sourceLabelKey(source))).join(' + ');

/** The export acts on a finding when it is active and at least one of its categories is on. */
export const isFindingInExport = (finding: FindingModel, excluded: ReadonlySet<Category>): boolean =>
  finding.status === 'active' && findingCategories(finding).some((category) => !excluded.has(category));

/**
 * The category a finding is drawn in: its first category that is still on, else its first one.
 * Unknown keys (a category deleted since the run) come back as `undefined`, which renders grey.
 */
export const findingDisplayCategory = (
  finding: FindingModel,
  categories: readonly CategorySummary[],
  excluded: ReadonlySet<Category>
): CategorySummary | undefined => {
  const keys = findingCategories(finding);
  const key = keys.find((category) => !excluded.has(category)) ?? keys[0];
  return categories.find((entry) => entry.key === key);
};
